import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Search, Filter, ChevronDown, ChevronUp,
  CheckCircle, Truck, MapPin, Navigation, Flag, AlertCircle,
  ChevronLeft, ChevronRight, Menu, TrendingUp, Clock, Box, Calendar, Copy, X, Edit, FileText, Eye, PlusIcon, Check
} from 'lucide-react';
import { fetchUserLoads, selectLoads, selectLoadById, selectActiveTab, cancelLoad, fetchLoadDocuments } from '../store/slices/loadSlice';
import { Link, useNavigate } from 'react-router-dom';
import SideNavigation from '../components/SideNavigation';
import useScreenSize from '../hooks/useScreenSize';
import { useDebounce } from '../hooks/useDebounce';
import LoadStatusTimeline from '../components/LoadStatusTimeline';
import { fetchNotifications } from '../store/slices/notificationSlice';
import { setActiveTab } from '../store/slices/loadSlice';
import EditLoadModal from '../components/EditLoadModal';
import ResubmitLoadModal from '../components/ResubmitLoadModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CancelLoadModal from '../components/CancelLoadModal';
import DocumentCard from '../components/DocumentCard';
import { LoadActionButtons } from '../components/LoadActionButtons';
import { computeFirstPickup, computeLastDelivery, getLoadDisplayId, getLoadUrlId, isLoadExpired } from '../utils/loadUtils';
import { formatDate } from '../utils/dateTimeUtils';
import { formatDateTime } from '../utils/formatters';
import { exceptionApi } from '../utils/api';

const STATUS_CONFIG = {
  open: {
    color: 'yellow',
    label: 'Open for Bids',
    icon: Package
  },
  expired: {
    color: 'orange',
    label: 'Expired',
    icon: AlertCircle
  },
  assigned: {
    color: 'blue',
    label: 'Awaiting Driver',
    icon: Truck
  },
  assigned_driver: {
    color: 'indigo',
    label: 'Driver Assigned',
    icon: MapPin
  },
  trip_started: {
    color: 'cyan',
    label: 'Trip Started',
    icon: Navigation
  },
  cancelled: {
    color: 'red',
    label: 'Cancelled',
    icon: X
  },
  completed: {
    color: 'green',
    label: 'Completed',
    icon: CheckCircle
  },
  
  getConfig: (status, load) => {
    if (!status) {
      return {
        color: 'gray',
        label: 'Status Unavailable',
        icon: AlertCircle,
        action: 'Update Status',
        next: null
      };
    }

    const cleanStatus = String(status).trim().toLowerCase();

    if (STATUS_CONFIG[cleanStatus]) {
      return STATUS_CONFIG[cleanStatus];
    }

    if (cleanStatus.startsWith('at_stop_')) {
      const stopNumber = parseInt(cleanStatus.split('_')[2]);
      const isLastStop = load?.stops && stopNumber === load.stops.length - 1;
      
      return {
        color: 'purple',
        label: `At Stop ${stopNumber + 1}${isLastStop ? ' (Final Stop)' : ''}`,
        icon: Navigation,
        action: isLastStop ? 'Complete Delivery' : 'Start Next Transit',
        next: isLastStop ? 'completed' : `in_transit_${stopNumber + 1}`
      };
    }
    
    if (cleanStatus.startsWith('in_transit_')) {
      return {
        color: 'orange',
        label: 'In Transit',
        icon: Flag,
        action: 'Mark At Next Stop',
        next: 'at_stop'
      };
    }

    return {
      color: 'gray',
      label: 'Status Unavailable',
      icon: AlertCircle,
      action: 'Update Status',
      next: null
    };
  }
};

const StatusBadge = ({ status, load, isMobile = false }) => {
  const config = STATUS_CONFIG.getConfig(status, load);
  const Icon = config.icon;
  
  // Map color names to specific Tailwind classes for the dot
  const dotColorClasses = {
    yellow: 'bg-yellow-500',
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500',
    cyan: 'bg-cyan-500',
    red: 'bg-red-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    gray: 'bg-gray-500'
  };

  const dotClass = dotColorClasses[config.color] || 'bg-gray-500';

  // Mobile: inline badge below header; Desktop: absolute positioned
  if (isMobile) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow-sm border border-gray-100"
      >
        <div className={`w-1.5 h-1.5 rounded-full ${dotClass} animate-pulse`} />
        <Icon className={`w-3 h-3 text-${config.color}-500`} />
        <span className="text-xs font-medium text-gray-900">
          {config.label}
        </span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`
        absolute top-0 right-0 
        transform translate-x-2 -translate-y-1/2 
        px-4 py-2
        rounded-full 
        bg-white/95 backdrop-blur-sm
        shadow-lg
        border border-gray-100
      `}
    >
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${dotClass} animate-pulse`} />
        <Icon className={`w-4 h-4 text-${config.color}-500`} />
        <span className="text-sm font-medium text-gray-900">
          {config.label}
        </span>
      </div>
    </motion.div>
  );
};

const TimeCard = ({ icon: Icon, label, date, time, address }) => {
  const { isMobile } = useScreenSize();
  
  return (
    <motion.div 
      className={`
        p-4 bg-white rounded-xl border border-gray-100 
        shadow-sm hover:shadow-md transition-shadow duration-200
        ${isMobile ? 'text-sm' : ''}
      `}
      whileHover={{ scale: 1.01 }}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg bg-blue-50 flex items-center justify-center`}>
            <Icon className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-500`} />
          </div>
          <div>
            <p className="text-gray-500 font-medium">{label}</p>
            <p className="font-semibold text-gray-900">
              {(() => {
                const formattedDate = formatDate(date);
                if (!formattedDate) return 'Invalid Date';
                
                // Convert MM/DD/YYYY to a more readable format
                const [month, day, year] = formattedDate.split('/');
                const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                
                return dateObj.toLocaleDateString(undefined, { 
                  weekday: isMobile ? 'short' : 'long',
                  year: 'numeric',
                  month: isMobile ? 'short' : 'long',
                  day: 'numeric'
                });
              })()}
              {time && (
                <span className="ml-2 text-gray-500">
                  {formatTime(time)}
                </span>
              )}
            </p>
          </div>
        </div>
        
        {address && (
          <div className={`${isMobile ? 'pl-11' : 'pl-14'}`}>
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-gray-600 break-words">{address}</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const DetailCard = ({ icon: Icon, label, value }) => {
  const { isMobile } = useScreenSize();
  
  return (
    <motion.div 
      className={`group p-${isMobile ? '3' : '4'} bg-white/60 backdrop-blur-sm rounded-xl border border-gray-100`}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300">
          <Icon className="w-4 h-4 text-blue-500" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </motion.div>
  );
};

const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
};

const ButtonVariants = {
  primary: "bg-blue-500 hover:bg-blue-600 text-white",
  secondary: "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200",
  danger: "bg-red-500 hover:bg-red-600 text-white",
  ghost: "bg-transparent hover:bg-gray-50 text-gray-600",
  disabled: "bg-gray-100 text-gray-400 cursor-not-allowed",
};

const Button = ({ 
  variant = "primary", 
  icon: Icon, 
  children, 
  disabled, 
  onClick, 
  className = "" 
}) => {
  const baseStyles = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm";
  const variantStyles = disabled ? ButtonVariants.disabled : ButtonVariants[variant];
  
  return (
    <motion.button
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      className={`${baseStyles} ${variantStyles} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </motion.button>
  );
};

// Helper to get exception border/styling classes based on severity
const getExceptionStyles = (severity) => {
  const styles = {
    low: {
      border: 'border-yellow-400',
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      icon: 'text-yellow-500',
      badge: 'bg-yellow-100 text-yellow-800'
    },
    medium: {
      border: 'border-orange-400',
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      icon: 'text-orange-500',
      badge: 'bg-orange-100 text-orange-800'
    },
    high: {
      border: 'border-red-400',
      bg: 'bg-red-50',
      text: 'text-red-700',
      icon: 'text-red-500',
      badge: 'bg-red-100 text-red-800'
    },
    critical: {
      border: 'border-red-500',
      bg: 'bg-red-50',
      text: 'text-red-800',
      icon: 'text-red-600',
      badge: 'bg-red-200 text-red-900'
    }
  };
  return styles[severity] || styles.medium;
};

const ExceptionBanner = ({ exceptions, isMobile }) => {
  const activeExceptions = exceptions?.filter(e => !e.is_resolved) || [];
  
  if (activeExceptions.length === 0) return null;

  const highestSeverity = activeExceptions.reduce((highest, exc) => {
    const order = { low: 0, medium: 1, high: 2, critical: 3 };
    return order[exc.severity] > order[highest] ? exc.severity : highest;
  }, 'low');

  const styles = getExceptionStyles(highestSeverity);
  const latestException = activeExceptions[0];

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className={`${styles.bg} border-t ${styles.border} px-4 py-3 -mx-3 md:-mx-6 mt-4`}
    >
      <div className="flex items-center gap-3">
        <AlertCircle className={`w-4 h-4 ${styles.icon} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${styles.text}`}>
              {activeExceptions.length === 1 ? 'Exception' : `${activeExceptions.length} Exceptions`}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
              {latestException.exception_type.replace(/_/g, ' ')}
            </span>
          </div>
          <p className={`text-xs ${styles.text} opacity-80 mt-0.5 truncate`}>
            {latestException.notes}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

const ExceptionDetails = ({ exceptions }) => {
  const activeExceptions = exceptions?.filter(e => !e.is_resolved) || [];
  const resolvedExceptions = exceptions?.filter(e => e.is_resolved) || [];
  const [showResolved, setShowResolved] = useState(false);
  
  if (activeExceptions.length === 0 && resolvedExceptions.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6"
    >
      {/* Active Exceptions */}
      {activeExceptions.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            Active Exceptions ({activeExceptions.length})
          </h4>
          {activeExceptions.map((exc) => {
            const styles = getExceptionStyles(exc.severity);
            return (
              <div 
                key={exc.id} 
                className={`p-4 rounded-xl border-l-4 ${styles.border} bg-white shadow-sm`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium ${styles.text}`}>
                        {exc.exception_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
                        {exc.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{exc.notes}</p>
                    <p className="text-xs text-gray-400 mt-2">
                      Reported {formatDateTime(exc.created_at)}
                      {exc.created_by_name && ` by ${exc.created_by_name}`}
                      {exc.is_automated && ' (Automated)'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Resolved Exceptions (Collapsible) */}
      {resolvedExceptions.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowResolved(!showResolved)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            {showResolved ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {resolvedExceptions.length} Resolved Exception{resolvedExceptions.length > 1 ? 's' : ''}
          </button>
          <AnimatePresence>
            {showResolved && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 space-y-2"
              >
                {resolvedExceptions.map((exc) => (
                  <div key={exc.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-gray-600">
                        {exc.exception_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{exc.notes}</p>
                    {exc.resolution_notes && (
                      <p className="text-xs text-green-600 mt-1">
                        Resolution: {exc.resolution_notes}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Resolved {exc.resolved_at && formatDateTime(exc.resolved_at)}
                      {exc.resolved_by_name && ` by ${exc.resolved_by_name}`}
                    </p>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

const LoadItem = ({ load, handleEditLoad, onCancelLoad, onResubmitLoad }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const currentLoad = useSelector(state => selectLoadById(state, load.id)) || load;
  const status = currentLoad.status.toLowerCase();
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  
  // Get active exceptions
  const activeExceptions = currentLoad.exceptions?.filter(e => !e.is_resolved) || [];
  const hasActiveException = activeExceptions.length > 0;
  const highestSeverity = activeExceptions.reduce((highest, exc) => {
    const order = { low: 0, medium: 1, high: 2, critical: 3 };
    return order[exc.severity] > order[highest] ? exc.severity : highest;
  }, 'low');
  const exceptionStyles = hasActiveException ? getExceptionStyles(highestSeverity) : null;
  
  // Use centralized isLoadExpired utility from loadUtils.js
  // This properly parses the ISO8601 pickup_datetime for accurate timezone handling
  const expired = isLoadExpired(currentLoad);

  const getRateConfirmationDoc = () => {
    return currentLoad.documents?.find(doc => 
      doc.document_type === 'rate_confirmation' || 
      doc.document_type_display?.toLowerCase().includes('rate confirmation')
    );
  };

  const rateConfDoc = getRateConfirmationDoc();

  const handleCancelClick = (e) => {
    e.stopPropagation();
    onCancelLoad(load);
  };

  const handleExpandDocuments = async () => {
    if (!documentsLoaded) {
      await dispatch(fetchLoadDocuments(load.id));
      setDocumentsLoaded(true);
    }
  };

  return (
    <motion.div
      layout="position"
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ 
        layout: { duration: 0.2 },
        opacity: { duration: 0.2 }
      }}
      className={`
        relative overflow-hidden
        bg-gradient-to-br from-white to-gray-50
        rounded-2xl 
        ${isMobile ? 'p-3' : 'p-6'}
        border-2 ${hasActiveException ? exceptionStyles.border : 'border-gray-100'}
        shadow-sm hover:shadow-md
        transition-all duration-300
        ${isExpanded ? 'shadow-lg' : 'shadow-sm'}
        ${hasActiveException ? 'ring-1 ring-opacity-20 ' + (
          highestSeverity === 'critical' ? 'ring-red-500' :
          highestSeverity === 'high' ? 'ring-red-400' :
          highestSeverity === 'medium' ? 'ring-orange-400' : 'ring-yellow-400'
        ) : ''}
      `}
    >
      {/* Status Badge - Only show absolute positioned on desktop */}
      {!isMobile && <StatusBadge status={expired ? 'expired' : status} load={currentLoad} />}

      <div className="relative z-10">
        {/* Header - Stacked layout for mobile */}
        <div className="flex flex-col gap-2">
          {/* Mobile: Status badge inline at top */}
          {isMobile && (
            <div className="flex justify-end -mb-1">
              <StatusBadge status={expired ? 'expired' : status} load={currentLoad} isMobile={true} />
            </div>
          )}
          
          <div className="flex items-start gap-2 sm:gap-3">
            <div className={`${isMobile ? 'w-9 h-9' : 'w-10 h-10'} rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0`}>
              <Package className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-blue-500`} />
            </div>
            <div className="min-w-0 flex-1">
              {/* Mobile: Stack title and ID; Desktop: Inline */}
              <div className={`${isMobile ? 'flex flex-col gap-1' : 'flex items-center gap-2 flex-wrap'}`}>
                <h3 className={`${isMobile ? 'text-sm' : 'text-lg'} font-semibold text-gray-900 truncate`}>
                  {currentLoad.load_type}
                </h3>
                <span className={`${isMobile ? 'text-[10px] px-1.5 py-0.5 w-fit' : 'text-xs px-2 py-1'} bg-gray-100 rounded-md text-gray-600 font-mono`}>
                  #{getLoadDisplayId(currentLoad)}
                </span>
              </div>
              <div className={`${isMobile ? 'mt-1.5' : 'mt-1'} flex items-start gap-1.5 text-gray-600`}>
                <MapPin className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} flex-shrink-0 mt-0.5`} />
                <p className={`${isMobile ? 'text-xs leading-tight' : 'text-sm'}`}>
                  {currentLoad.origin_city}, {currentLoad.origin_state} → {currentLoad.destination_city}, {currentLoad.destination_state}
                </p>
              </div>
              {currentLoad.assigned_to_carrier && (
                <div className={`${isMobile ? 'mt-1.5' : 'mt-2'} flex items-center gap-1.5 text-gray-500`}>
                  <Truck className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'}`}>{currentLoad.carrier_name || 'Unknown Carrier'}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action bar - Optimized for mobile */}
        <div className={`mt-3 pt-3 border-t border-gray-100 ${isMobile ? 'space-y-2' : 'flex items-center justify-between'}`}>
          {/* Primary Actions */}
          <div className={`flex ${isMobile ? 'flex-wrap gap-2' : 'items-center space-x-3'}`}>
            {expired ? (
              currentLoad.resubmitted ? (
                <div className={`flex items-center ${isMobile ? 'px-2.5 py-1.5' : 'px-4 py-2'} bg-gray-50/50 backdrop-blur-sm rounded-lg border border-gray-100 opacity-60`}>
                  <Check className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-green-500 mr-1.5`} />
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 font-medium`}>
                    Resubmitted
                  </span>
                </div>
              ) : (
                <Button
                  variant="primary"
                  icon={PlusIcon}
                  onClick={() => onResubmitLoad(currentLoad)}
                  className={`${isMobile ? 'text-xs py-1.5 px-2.5' : ''}`}
                >
                  Resubmit
                </Button>
              )
            ) : status === 'open' ? (
              <Button
                variant="secondary"
                icon={Edit}
                onClick={() => handleEditLoad(currentLoad)}
                className={`${isMobile ? 'text-xs py-1.5 px-2.5' : ''}`}
              >
                {isMobile ? 'Edit' : 'Edit Load'}
              </Button>
            ) : (
              <div className={`flex items-center ${isMobile ? 'px-2.5 py-1.5' : 'px-4 py-2'} bg-gray-50/50 backdrop-blur-sm rounded-lg border border-gray-100`}>
                <Clock className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-400 mr-1.5`} />
                <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 font-medium`}>Assigned</span>
              </div>
            )}

            <LoadActionButtons 
              load={currentLoad} 
              isMobile={isMobile}
              canShare={true}
            />
          </div>

          {/* Secondary Actions Row */}
          <div className={`flex items-center ${isMobile ? 'justify-between' : 'space-x-3'}`}>
            {/* Cancel Button / Status */}
            {['open', 'assigned', 'assigned_driver'].includes(status) ? (
              <Button
                variant="ghost"
                icon={X}
                onClick={handleCancelClick}
                className={`text-red-600 hover:bg-red-50 border border-red-200 transition-colors duration-200 ${isMobile ? 'text-xs py-1.5 px-2.5' : ''}`}
              >
                {isMobile ? 'Cancel' : 'Cancel Load'}
              </Button>
            ) : (
              <div className={`flex items-center ${isMobile ? 'px-2 py-1' : 'px-4 py-2'} bg-gray-50/50 backdrop-blur-sm rounded-lg border border-gray-100`}>
                <X className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-gray-400 mr-1`} />
                <span className={`${isMobile ? 'text-[10px]' : 'text-sm'} text-gray-600 font-medium`}>Can't Cancel</span>
              </div>
            )}

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-lg hover:bg-gray-100 text-gray-400 transition-colors`}
              aria-label={isExpanded ? "Show less" : "Show more"}
            >
              {isExpanded ? (
                <ChevronUp className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
              ) : (
                <ChevronDown className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Exception Banner - Shows in collapsed view */}
        {!isExpanded && hasActiveException && (
          <ExceptionBanner exceptions={currentLoad.exceptions} isMobile={isMobile} />
        )}

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className={`mt-4 ${isMobile ? 'space-y-4' : 'space-y-6'}`}
            >
              {/* Exception Details - Show all exceptions with full details */}
              {currentLoad.exceptions?.length > 0 && (
                <ExceptionDetails exceptions={currentLoad.exceptions} />
              )}

              {/* Enhanced Timeline */}
              <div className="mb-8">
                <LoadStatusTimeline loadId={currentLoad.id} isCustomer={true} />
              </div>

              {/* Load Details Grid - Modified for mobile */}
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-2 md:grid-cols-3 gap-6'} mb-8`}>
                <DetailCard
                  icon={Package}
                  label="Weight"
                  value={`${currentLoad.weight} lbs`}
                />
                <DetailCard
                  icon={Box}
                  label="Dimensions"
                  value={currentLoad.dimensions ? 
                    `${currentLoad.dimensions.length}x${currentLoad.dimensions.width}x${currentLoad.dimensions.height}` : 
                    'N/A'
                  }
                />
                <DetailCard
                  icon={Package}
                  label="Pieces"
                  value={currentLoad.pieces}
                />
              </div>

              {/* Schedule and Location Information - Modified for mobile */}
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-6 mb-8`}>
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-500" />
                    Initial Pickup Location
                  </h4>
                  {(() => {
                    const fp = computeFirstPickup(currentLoad);
                    return (
                      <TimeCard
                        icon={Calendar}
                        label="Pickup Appointment"
                        date={fp.date}
                        time={fp.time}
                        address={`${fp.address}, ${fp.city}, ${fp.state} ${fp.zip || ''}`.trim()}
                      />
                    );
                  })()}
                </div>
                
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Flag className="w-5 h-5 text-green-500" />
                    Final Delivery Location
                  </h4>
                  {(() => {
                    const ld = computeLastDelivery(currentLoad);
                    return (
                      <TimeCard
                        icon={Calendar}
                        label="Delivery Appointment"
                        date={ld.date}
                        time={ld.time}
                        address={`${ld.address}, ${ld.city}, ${ld.state} ${ld.zip || ''}`.trim()}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* Resubmitted Status Card */}
              {currentLoad.resubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 mb-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-800">Load Resubmitted</h4>
                      <p className="text-green-700 text-sm">This expired load has been resubmitted with updated dates</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Special Requirements Card */}
              {currentLoad.special_requirements && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100"
                >
                  <h4 className="text-lg font-semibold text-amber-800 mb-4">Special Requirements</h4>
                  <p className="text-amber-700">{currentLoad.special_requirements}</p>
                </motion.div>
              )}

              {/* Carrier Information if assigned */}
              {currentLoad.assigned_to_carrier && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100"
                >
                  <h4 className="text-lg font-semibold text-blue-800 mb-4">Assigned Carrier</h4>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-blue-900 font-medium">{currentLoad.carrier_name}</p>
                      <p className="text-sm text-blue-600">MC# {currentLoad.carrier_mc_number}</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Documents Section */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Load Documents
                </h4>
                
                <div className="grid grid-cols-1 gap-3">
                  {!documentsLoaded ? (
                    <button 
                      onClick={handleExpandDocuments}
                      className="text-blue-500 hover:text-blue-600 text-sm flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" /> View Documents
                    </button>
                  ) : (
                    <>
                      {load.documents?.map((doc) => (
                        <DocumentCard 
                          key={doc.id} 
                          document={doc}
                          isMobile={isMobile}
                        />
                      ))}
                      {(!load.documents || load.documents.length === 0) && (
                        <p className="text-sm text-gray-500 italic">No documents available</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const DashboardCard = ({ icon: Icon, title, value, trend, trendValue }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="bg-blue-100 rounded-full p-3">
          <Icon className="h-6 w-6 text-blue-500" />
        </div>
        {trend && (
          <div className={`flex items-center ${trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
            {trend === 'up' ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingUp className="h-4 w-4 mr-1 transform rotate-180" />}
            <span className="text-sm font-medium">{trendValue}</span>
          </div>
        )}
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

// Status options for filtering within the active tab
const STATUS_FILTER_OPTIONS = {
  active: [
    { value: 'all', label: 'All Active' },
    { value: 'open', label: 'Open for Bids' },
    { value: 'assigned', label: 'Awaiting Driver' },
    { value: 'assigned_driver', label: 'Driver Assigned' },
    { value: 'trip_started', label: 'Trip Started' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'at_stop', label: 'At Stop' },
  ],
  expired: [{ value: 'all', label: 'All Expired' }],
  completed: [{ value: 'all', label: 'All Completed' }],
  cancelled: [{ value: 'all', label: 'All Cancelled' }],
};

const CustomerShipments = () => {
  const dispatch = useDispatch();
  const loads = useSelector(selectLoads);
  const activeTab = useSelector(selectActiveTab);
  const { pagination } = useSelector((state) => state.load);
  const [sortOption, setSortOption] = useState('date_desc');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // Debounce search by 500ms
  const [filteredLoads, setFilteredLoads] = useState([]);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [currentPage, setCurrentPage] = useState(1);
  const [dashboardData, setDashboardData] = useState(null);
  const [editModalLoad, setEditModalLoad] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedLoadForCancel, setSelectedLoadForCancel] = useState(null);
  const cancelStatus = useSelector(state => state.load.cancelStatus);
  const [resubmitModalLoad, setResubmitModalLoad] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Reset to page 1 when filters change (but not when page itself changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, activeTab, statusFilter, sortOption]);

  /**
   * Server-Side Pagination & Filtering Architecture
   * ================================================
   * All filtering, sorting, and pagination happens server-side for scalability.
   * This ensures pagination counts match displayed results and supports millions of loads.
   * 
   * - Search: Debounced 500ms, searches id, origin, destination, load_type
   * - Status: Maps to backend filters (active, expired, completed, cancelled, etc.)
   * - Sub-filters: When active tab has status filter, it's sent to backend
   * - Sorting: Handled by backend based on sortBy and sortOrder
   * - Expiry: Backend excludes expired loads from 'active', includes in 'expired'
   */
  useEffect(() => {
    dispatch(fetchUserLoads({ 
      page: currentPage, 
      perPage: 10, 
      status: activeTab === 'active' && statusFilter !== 'all' ? statusFilter : activeTab,
      search: debouncedSearchQuery,
      sortBy: sortOption.startsWith('pickup_date') ? 'pickup_date' : 'created_at',
      sortOrder: sortOption.endsWith('desc') ? 'desc' : 'asc',
      include: ['documents']
    }));
  }, [dispatch, currentPage, activeTab, statusFilter, debouncedSearchQuery, sortOption]);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  // Check for automated exceptions (late arrivals) on page load
  useEffect(() => {
    const checkForExceptions = async () => {
      try {
        const response = await exceptionApi.checkExceptions();
        if (response.data?.exceptions_created > 0) {
          toast.info(
            `🔔 ${response.data.exceptions_created} late arrival${response.data.exceptions_created > 1 ? 's' : ''} detected`,
            { autoClose: 5000 }
          );
          // Refresh loads to get updated exception data
          dispatch(fetchUserLoads({ 
            page: currentPage, 
            perPage: 10, 
            status: activeTab,
            include: ['documents']
          }));
        }
      } catch (error) {
        // Silently fail - this is a background check
        console.debug('Exception check failed:', error);
      }
    };
    
    checkForExceptions();
  }, []); // Only run on initial mount

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  // Simplified client-side filtering - server handles status, search, and sorting
  // This just ensures UI consistency while data is loading
  const filterLoads = useCallback(() => {
    // Server now handles all filtering, so we just use loads as-is
    // This ensures pagination counts match the displayed results
    setFilteredLoads(loads);
  }, [loads]);

  useEffect(() => {
    filterLoads();
  }, [filterLoads]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Reset to page 1 when searching
    // The useEffect will handle fetching with the new search query
    setCurrentPage(1);
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab));
    setCurrentPage(1);
    setStatusFilter('all'); // Reset status filter when changing tabs
    // useEffect will handle fetching with the new tab
  };

  const handleEditLoad = (load) => {
    setEditModalLoad(load);
  };

  const handleCloseEditModal = () => {
    setEditModalLoad(null);
  };

  const handleResubmitLoad = (load) => {
    setResubmitModalLoad(load);
  };

  const handleCloseResubmitModal = () => {
    setResubmitModalLoad(null);
  };

  const onCancelLoad = (load) => {
    setSelectedLoadForCancel(load);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (selectedLoadForCancel) {
      try {
        const result = await dispatch(cancelLoad(getLoadUrlId(selectedLoadForCancel))).unwrap();
        setShowCancelModal(false);
        setSelectedLoadForCancel(null);
        toast.success(`Load ${getLoadDisplayId(selectedLoadForCancel)} cancelled successfully`);
      } catch (error) {
        toast.error(error?.message || 'Failed to cancel load. Please try again.');
        setShowCancelModal(true);
      }
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedLoadForCancel(null);
  };

  return (
    <div className="bg-gray-100 min-h-screen flex">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />

      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 
        ${isMobile ? 'px-2' : (sidebarOpen ? 'ml-64 px-6' : 'ml-16 px-6')}`}
      >
        <div className="max-w-7xl mx-auto py-4">
          {/* Mobile-optimized header */}
          <div className={`flex items-center justify-between mb-4 ${isMobile ? 'px-2' : ''}`}>
            {isMobile && (
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg bg-white shadow-sm hover:bg-gray-50 transition-colors"
                aria-label="Toggle menu"
              >
                <Menu className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <h1 className={`font-bold text-gray-900 ${isMobile ? 'text-xl ml-2' : 'text-2xl'}`}>
              Shipments
            </h1>
          </div>

          {/* Mobile-optimized search and filters */}
          <div className={`space-y-3 mb-6 ${isMobile ? 'px-2' : ''}`}>
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search shipments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
            </form>

            {/* Mobile-optimized tab buttons */}
            <div className={`flex flex-wrap gap-2 ${isMobile ? 'justify-center' : ''}`}>
              {['active', 'expired', 'completed', 'cancelled'].map((tab) => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'primary' : 'secondary'}
                  onClick={() => handleTabChange(tab)}
                  className={`${isMobile ? 'text-sm py-1.5 px-3' : ''}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Button>
              ))}
            </div>

            {/* Status filter and sort dropdowns */}
            <div className={`flex ${isMobile ? 'flex-col w-full gap-2' : 'items-center gap-3'}`}>
              {/* Status Filter - Only show when there are multiple options */}
              {STATUS_FILTER_OPTIONS[activeTab]?.length > 1 && (
                <div className={`relative ${isMobile ? 'w-full' : 'min-w-[160px]'}`}>
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg py-2 pl-9 pr-3 bg-white appearance-none cursor-pointer hover:border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  >
                    {STATUS_FILTER_OPTIONS[activeTab]?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              )}
              
              {/* Sort dropdown */}
              <div className={`relative ${isMobile ? 'w-full' : 'min-w-[160px]'}`}>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg py-2 px-3 bg-white appearance-none cursor-pointer hover:border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  <option value="date_desc">Newest First</option>
                  <option value="date_asc">Oldest First</option>
                  <option value="pickup_date_asc">Earliest Pickup</option>
                  <option value="pickup_date_desc">Latest Pickup</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Shipments List */}
          <div className="space-y-4 relative">
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredLoads.map((load) => (
                <motion.div
                  key={load.id}
                  layout="position"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{
                    duration: 0.2,
                    ease: "easeInOut"
                  }}
                >
                  <LoadItem 
                    load={load} 
                    handleEditLoad={handleEditLoad}
                    onCancelLoad={onCancelLoad}
                    onResubmitLoad={handleResubmitLoad}
                  />
                </motion.div>
              ))}

              {/* Optimize empty state animation */}
              {filteredLoads.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="text-center py-8 bg-white rounded-lg shadow"
                >
                  <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No shipments found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search or filter to find what you're looking for.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Pagination - Mobile Optimized */}
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-md bg-white shadow-sm disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <span className="px-4 py-2 text-sm">
                Page {currentPage} of {pagination.pages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.pages}
                className="p-2 rounded-md bg-white shadow-sm disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </main>

      {/* Add EditLoadModal */}
      <AnimatePresence>
        {editModalLoad && (
          <EditLoadModal
            load={editModalLoad}
            isOpen={Boolean(editModalLoad)}
            onClose={handleCloseEditModal}
          />
        )}
      </AnimatePresence>

      {/* Add ResubmitLoadModal */}
      <AnimatePresence>
        {resubmitModalLoad && (
          <ResubmitLoadModal
            load={resubmitModalLoad}
            isOpen={Boolean(resubmitModalLoad)}
            onClose={handleCloseResubmitModal}
          />
        )}
      </AnimatePresence>

      {/* Add CancelLoadModal */}
      <CancelLoadModal
        isOpen={showCancelModal}
        onClose={handleCloseCancelModal}
        onConfirm={handleConfirmCancel}
        load={selectedLoadForCancel}
        isLoading={cancelStatus === 'loading'}
      />

      {/* Add ToastContainer */}
      <ToastContainer 
        position={isMobile ? "bottom-center" : "top-right"}
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default CustomerShipments;
